import * as React from 'react';

import { cn } from './lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

/**
 * {@link Input}, multi-line. Promoted from the operator app (IMM-981), where
 * five bare `<textarea>`s each carried a hand-written class list: two copies of
 * `Input`'s, which drift the moment `Input` changes, and three prompt editors
 * with a `ring-2` focus the rest of the system does not use. A prompt editor
 * keeps its mono face through `className`; the ring and the invalid state are
 * the kit's.
 *
 * Surface, border, focus ring and disabled treatment are `Input`'s, so the two
 * read as one control family. The invalid state is the web-app `Textarea`'s,
 * which IMM-739.2 pinned and this replaces: `border-danger-600`, and the border
 * stays put on focus while the danger ring carries the state. `h-control-md` is
 * left out — a textarea's height is its `rows`.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid = false, rows = 4, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid ? true : props['aria-invalid']}
      className={cn(
        'flex w-full rounded-md border bg-surface px-3 py-2 text-base font-sans text-text-primary',
        'placeholder:text-text-placeholder',
        'transition-colors duration-micro ease-out',
        'focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        invalid
          ? 'border-danger-600 focus-visible:shadow-focus-danger'
          : 'border-border focus-visible:border-interactive focus-visible:shadow-focus',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export { Textarea };
